<!-- This component wrapper is designed to handle the rendering of elements in an infinitely scrollable list. -->
<!-- Elements that are not visible within the viewport (+ a buffer) will be unmounted from the DOM. -->
<!-- This prevents the DOM from getting overloaded with hundreds/thousands of expensive components. -->

<template>
  <div :ref="`loader-wrapper-${identifier}`" :style="{width: wrapperDimensions.width, height: wrapperDimensions.height}"
  :class="{'opacity-0': enableInitialMountFadeIn}">
    <transition>
      <div v-if="contentInView">
        <slot :handleMediaLoaded="handleMediaLoaded" />
      </div>
    </transition>
  </div>
</template>

<script>
import { observe, unobserve } from '../../utils/scrollUnloadObserver'

export default {
  name: 'BaseDynamicLoader',
  props: {
    // A unique key to identify the element
    identifier: {
      type: String,
      required: true
    },
    // CSS selector for the element that will watch for the target element (scrolling container)
    observerElmSelector: {
      type: String,
      default: '.main-layout'
    },
    // The buffer determines how far from the viewport the element should be before loading/unloading
    buffer: {
      type: Number,
      default: 200
    },
    // Whether or not to fade in the content on initial mount
    enableInitialMountFadeIn: {
      type: Boolean,
      default: true
    },
    // Flag this to make sure height doesn't reset to 'auto' until the slot's media is loaded
    enableMediaLoadBuffer: {
      type: Boolean,
      default: false
    },
    staticDimensions: {
      type: Object, // e.g. { width: 'auto', height: 'auto' }
    },
    useXAxis: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      contentInView: true,
      wrapperDimensions: this.staticDimensions || { width: 'auto', height: 'auto' },
      unloadTimeout: null,
      intersectingNow: true
    }
  },
  mounted () {
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        const loaderWrapper = this.$refs[`loader-wrapper-${this.identifier}`]
        if (this.enableInitialMountFadeIn) {
          setTimeout(() => { loaderWrapper.classList.add('onmount-fade-in') }, 10)
        }
        const root = document.querySelector(this.observerElmSelector) || null
        const observerOptions = {
          root: root,
          rootMargin: this.useXAxis ? `0px ${this.buffer}px 0px ${this.buffer}px` : `${this.buffer}px 0px ${this.buffer}px 0px`,
          threshold: 0
        }
        observe(loaderWrapper, this.handleIntersection, this, observerOptions)
      })
    })
  },
  beforeDestroy () {
    const loaderWrapper = this.$refs[`loader-wrapper-${this.identifier}`]
    unobserve(loaderWrapper)
    if (this.unloadTimeout) clearTimeout(this.unloadTimeout)
  },
  methods: {
    handleIntersection (entry) {
      this.intersectingNow = entry.isIntersecting
      if (entry.isIntersecting) {
        if (this.unloadTimeout) clearTimeout(this.unloadTimeout)
        // Reset the wrapper dimensions to 'auto' to allow the content to expand, unless media load buffer is enabled
        if (!this.staticDimensions && !this.enableMediaLoadBuffer) {
          this.wrapperDimensions = { width: 'auto', height: 'auto' }
        }
        this.contentInView = true
        entry.target.classList.add('content-in-view')
      } else {
        // Debounce the unload logic
        if (this.unloadTimeout) clearTimeout(this.unloadTimeout)
        this.unloadTimeout = setTimeout(() => {
          // Ensure the content is still out of view
          const contentRect = entry.boundingClientRect
          if (!this.intersectingNow) {
            if (!this.staticDimensions) {
              this.wrapperDimensions = { width: `${contentRect.width}px`, height: `${contentRect.height}px` }
            }
            this.contentInView = false
            entry.target.classList.remove('content-in-view')
          }
        }, 500)
      }
    },
    handleMediaLoaded () {
      // In the case where we want to maintain the height of the wrapper until the slot's media is loaded.
      // Notably used for the LibraryCard component in the AdvertisementList. This method is called from the slot.
      if (!this.staticDimensions) {
        this.wrapperDimensions = { width: 'auto', height: 'auto' }
      }
    }
  }
}
</script>

<style scoped>
/* Have content fade-in to make the load less jarring */
.v-enter-active {
  transition: opacity 75ms ease-in-out;
}
.v-enter, .v-enter-from {
  opacity: 0;
}
.v-enter-to {
  opacity: 1;
}
.onmount-fade-in {
  animation: fadeIn 75ms ease-in-out 1 forwards;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>